import RichText from "@/components/RichText";
import { CONTAINER } from "../tokens";

// Carrousel de réalisations : les cartes sont DUPLIQUÉES une fois et la
// piste défile de -50% (moins la moitié du gap, qui compte en double une
// fois le jeu dupliqué) — la boucle est donc sans couture, exactement comme
// le handoff. Le masque en dégradé estompe les deux bords.
//
// `prefers-reduced-motion` coupe l'animation : le handoff signale lui-même
// cette prise en charge comme « à ajouter à l'implémentation ». La piste
// reste alors défilable à la main (overflow-x auto).
const SPEEDS = { slow: "60s", normal: "42s", fast: "28s" };

// Emplacement de capture non encore téléversé. Le motif hachuré est celui
// que le handoff utilise déjà pour les logos clients et les avatars d'avis :
// sans lui, un aplat ivoire sur fond ivoire était invisible, et la carte
// paraissait vide — on croyait la section cassée.
const HATCH = {
  backgroundImage:
    "repeating-linear-gradient(135deg, rgb(var(--c-ink, 22 21 15) / 0.09) 0 2px, transparent 2px 10px)",
};
const MASK = "linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent)";

export default function WorkCarouselSection({ props = {}, blocks = [] }) {
  const items = (blocks || []).filter((b) => b.type === "work_item" && b.enabled !== false);
  const duration = SPEEDS[props.carousel_speed] || SPEEDS.normal;

  // Vidée de tout son contenu depuis l'éditeur, la section ne laissait qu'un
  // espace de 92px invisible entre les Tarifs et les Avis — impossible à
  // distinguer d'une section absente, et donc de comprendre qu'il n'y a
  // qu'un titre à ressaisir. Même garde que PriceListSection : plus rien à
  // montrer, on ne montre rien.
  if (!items.length && !props.heading && !props.subheading) return null;

  return (
    <section id="realisations" className={`${CONTAINER} pt-[92px] scroll-mt-24`}>
      <div className="grid md:grid-cols-2 gap-9 items-end mb-10">
        <RichText
          as="h2"
          className="font-display font-semibold text-[clamp(29px,3.5vw,43px)] leading-[1.06] tracking-[-0.03em] text-ink [text-wrap:pretty]"
          html={props.heading}
        />
        <RichText
          as="p"
          preserveParagraphs
          className="text-[16.5px] leading-[1.6] text-ink/[0.6] max-w-[440px]"
          html={props.subheading}
        />
      </div>

      {items.length > 0 && (
        <>
          {/* Keyframes portées par la section : une <style> locale évite
              d'ajouter une règle globale à App.css pour un seul moteur. */}
          <style>{`
            @keyframes ce-marquee { from { transform: translateX(0); } to { transform: translateX(calc(-50% - 9px)); } }
            .ce-track { animation: ce-marquee var(--ce-marquee-duration, 42s) linear infinite; }
            .ce-track:hover { animation-play-state: paused; }
            @media (prefers-reduced-motion: reduce) { .ce-track { animation: none; } }
          `}</style>
          <div
            className="overflow-hidden -mx-[18px] md:-mx-7 px-[18px] md:px-7"
            style={{ maskImage: MASK, WebkitMaskImage: MASK }}
          >
            <div className="ce-track flex gap-[18px] w-max" style={{ "--ce-marquee-duration": duration }}>
              {[...items, ...items].map((b, i) => (
                <article
                  key={`${b.id}-${i}`}
                  // Une seule des deux copies porte la clé d'édition, sinon
                  // l'éditeur surlignerait deux nœuds pour le même bloc.
                  data-block-key={i < items.length ? b.id : undefined}
                  aria-hidden={i >= items.length ? "true" : undefined}
                  className="flex-[0_0_clamp(280px,32vw,400px)] border-control border-ink/[0.14] rounded-card overflow-hidden flex flex-col bg-white"
                >
                  <div
                    className="w-full aspect-[16/10] bg-sand"
                    style={b.props?.image_url ? undefined : HATCH}
                  >
                    {b.props?.image_url && (
                      <img
                        src={b.props.image_url}
                        alt={b.props?.title ? `${b.props.title}` : ""}
                        loading="lazy"
                        className="block w-full h-full object-cover object-top"
                      />
                    )}
                  </div>
                  <div className="pt-5 px-[22px] pb-[22px]">
                    <div className="font-display text-[12px] tracking-[0.08em] uppercase text-ink/[0.55]">
                      {b.props?.category}
                    </div>
                    <RichText
                      as="div"
                      className="font-display font-semibold text-[19px] tracking-[-0.015em] mt-2.5 text-ink"
                      html={b.props?.title}
                    />
                    <p className="mt-2 text-[14.5px] leading-[1.55] text-ink/[0.62]">{b.props?.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
